<template>
  <div class="view-register-releases">
    <page-header
      :title="form.id ? 'Editar lançamento' : 'Cadastrar lançamento'"
      subtitle="Configure como o seu cliente verá o próximo lançamento do seu produto."
    />

    <div class="my-9 text-center" v-if="loading">
      <b-spinner variant="primary" />
    </div>

    <page-container aside v-else>
      <template #aside>
        <div class="text-neutral-600 functional-1 mb-2">
          Imagem de lançamento
          <span class="text-negative-700">*</span>
        </div>
        <bp-input-file
          is-image
          required
          v-model="form.cover"
          dimensions="800 x 800"
          title="Imagem de lançamento"
          supporting-text="A imagem será utilizada para a capa de lançamento."
        />
      </template>

      <div class="d-flex flex-column gap-5">
        <bp-input
          v-model="form.name"
          required
          id="name-product"
          label="Nome do produto"
          placeholder="Prefira um nome que chame atenção do público."
          description="Atenção! Este é o nome que exibiremos para os seus compradores."
          :errors="errors.name"
          :limit="64"
        />

        <bp-input
          v-model="form.url"
          required
          label="URL"
          id="name-product"
          placeholder="Ex.: https://www.blitzpay.com.br/produto/"
          description="Adicione a URL da página do seu produto."
          :errors="errors.url"
        />

        <bp-input
          v-model="form.title"
          required
          id="name-releases"
          label="Nome do lançamento"
          placeholder="Escolha um nome para o lançamento deste produto."
          description="Este nome aparecerá para seus clientes no card de lançamento na Blitzpay Club."
          :errors="errors.title"
          :limit="120"
        />

        <bp-input
          v-model="form.description"
          rows="6"
          textarea
          required
          id="description-product"
          label="Descrição do produto para lançamento"
          placeholder="Explique de forma breve do que se trata o seu produto."
          description="Esta é a descrição que exibiremos na capa de lançamento do seu produto."
          :errors="errors.description"
          :limit="980"
        />
      </div>

      <div class="row my-5">
        <div class="col-lg-6">
          <form-group
            v-model="releaseDate"
            is-date
            required
            readonly
            id="date"
            :min-date="new Date()"
            placeholder="00/00/0000"
            label="Data do lançamento"
          />
        </div>
        <div class="col-lg-6">
          <bp-input
            v-model="releaseTime"
            masked
            required
            id="time"
            mask="##:##"
            placeholder="00:00"
            label="Horário de abertura do checkout"
            :errors="errors.release_at"
          />
        </div>
      </div>

      <bp-input
        v-model="form.display_days"
        number
        outer-buttons
        id="standard-time"
        placeholder="Digite a quantidade em dias"
        label="Período de exibição no Blitzpay club após o lançamento."
        :min="0"
        :errors="errors.display_days"
      />

      <div
        class="d-flex flex-column flex-lg-row justify-content-lg-between gap-3 mt-7"
      >
        <bp-button
          size="xl"
          label="Salvar"
          class="order-lg-1"
          :disabled="!isValid"
          :loading="loadingSubmit"
          @click="submitForm"
        />
        <bp-button
          size="xl"
          color="neutral"
          label="Cancelar"
          route="releases"
          variant="outlined"
          icon="fa-light fa-angle-left"
        />
      </div>
    </page-container>
  </div>
</template>

<script>
import { formFields } from "@/functions/helpers";
import moment from "moment";
import { mapState } from "vuex";

export default {
  data() {
    return {
      releaseDate: null,
      releaseTime: null,
      loadingSubmit: false,
      ...formFields([
        "cover",
        "name",
        "url",
        "title",
        "description",
        "release_at",
        "display_days"
      ])
    };
  },
  computed: {
    ...mapState("membersReleases", ["releases"]),
    isValid() {
      return (
        this.form.cover &&
        this.form.name &&
        this.form.name.length <= 64 &&
        this.form.url &&
        this.form.title &&
        this.form.description &&
        this.form.description.length <= 980 &&
        this.releaseDate &&
        this.releaseDate.length == 10 &&
        this.releaseTime &&
        this.releaseTime.length == 5 &&
        (!this.form.display_days || this.form.display_days > 0)
      );
    }
  },
  methods: {
    submitForm() {
      let params = { ...this.form };

      params.release_at = moment(
        `${this.releaseDate} ${this.releaseTime}`,
        "DD/MM/YYYY HH:mm"
      ).format("YYYY-MM-DD[T]HH:mm");

      if (!params.display_days) {
        params.display_days = null;
      }
      let action = this.$route.params?.id
        ? "membersReleases/put"
        : "membersReleases/post";

      this.loadingSubmit = true;

      this.$store
        .dispatch(action, params)
        .then(() => {
          this.$message.success("O lançamento foi salvo!");
          this.$router.push({ name: "releases" });
        })
        .catch(error => {
          this.$message.error("Erro ao salvar o lançamento");
          this.errors = error.response.data;
          if (
            this.errors.release_at &&
            this.errors.release_at[0].match("Formato inválido")
          )
            this.errors.release_at = ["Formato inválido para data e hora."];
        })
        .finally(() => {
          this.loadingSubmit = false;
        });
    }
  },
  mounted() {
    if (this.$route.params?.id) {
      this.loading = true;
      this.$store
        .dispatch("membersReleases/getById", this.$route.params.id)
        .then(response => {
          const release_at = moment.utc(response.release_at);
          this.form = {
            ...response
          };
          this.releaseDate = release_at.format("DD/MM/YYYY");
          this.releaseTime = release_at.format("HH:mm");
        })
        .finally(() => (this.loading = false));
    }
  }
};
</script>

<style lang="scss" scoped>
.view-register-releases {
  .icon-angle-right,
  .icon-angle-left {
    font-size: 24px;
    margin-bottom: 3px;
  }

  ::v-deep {
    .form-group {
      padding: 0;
      label {
        color: $neutral-600;
        font-size: 14px;
        margin-bottom: 3px;
      }
      .form-control {
        height: 40px;
        min-height: 40px;
        position: relative;
        border-color: $neutral-300;
        font-size: 16px;
        color: $neutral-900;
      }
      .date-picker-wrapper {
        top: 24px;
      }
    }
  }
}
</style>
